import { locationsService } from '@/services/locationService/locationService';
import { walletService } from '@/services/walletManagementService/walletService';
import {
  CountryListResponse,
  ProjectList,
  WalletFilterParams,
  WalletInfoCount,
  WalletTransactionsResponse,
  WalletUpload,
} from '@/types/wallet.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface walletState {
  loading: boolean;
  loadingInfoCount: boolean;
  error: string | null;
  data: WalletTransactionsResponse | null;
  walletInfoCount: WalletInfoCount | null;
  xlsFile: File | null;
  countryList: CountryListResponse | null;
  projectList: ProjectList[] | null;
  totalPages: number | null;
}

const initialState: walletState = {
  loading: false,
  loadingInfoCount: false,
  error: null,
  data: null,
  walletInfoCount: null,
  xlsFile: null,
  countryList: null,
  projectList: [],
  totalPages: 0,
};
export const createWallets = createAsyncThunk(
  'wallet/create',
  async (data: WalletUpload, thunkAPI) => {
    try {
      return await walletService.createWallets(data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Error Adding Transactions');
    }
  },
);

export const getAllTransactions = createAsyncThunk<WalletTransactionsResponse, WalletFilterParams>(
  'wallet/getAllTransactions',
  async (params: WalletFilterParams, thunkAPI) => {
    try {
      const response = await walletService.getWalletList(params);
      return response;
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error creating project';

      return thunkAPI.rejectWithValue(apiMessage);
    }
  },
);

export const getWalletInfoCount = createAsyncThunk<WalletInfoCount, void>(
  'wallet/getcount',
  async (_, thunkAPI) => {
    try {
      const response = await walletService.getWalletInfoCount();
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || 'Failed to fetch transactions',
      );
    }
  },
);

export const getAllCountryList = createAsyncThunk<CountryListResponse, void>(
  'wallet/getCountries',
  async (_, thunkAPI) => {
    try {
      const response = (await locationsService.getCountries()) as CountryListResponse;
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to fetch countries');
    }
  },
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearWalletsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.totalPages = action.payload.meta.totalPages;
      })
      .addCase(getAllTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getWalletInfoCount.pending, (state) => {
        state.loadingInfoCount = true;
      })
      .addCase(getWalletInfoCount.fulfilled, (state, action) => {
        state.loadingInfoCount = false;
        state.walletInfoCount = action.payload;
      })
      .addCase(getWalletInfoCount.rejected, (state, action) => {
        state.loadingInfoCount = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearWalletsError } = walletSlice.actions;
export default walletSlice.reducer;
